import { createContext, useState, useEffect, useMemo } from 'react';
import { onlineManager } from '@tanstack/react-query';

import { OnlineStatus } from 'types/onlineStatus';
import { ping } from 'utils/api/backend';

interface IProps {
  children: JSX.Element;
}

export const OnlineStatusContext = createContext<OnlineStatus>({
  online: false,
  lastOnline: new Date('1970-01-01T00:00:00Z')
});

export const OnlineStatusContextProvider = ({ children }: IProps): JSX.Element => {
  const [browserOnline, setBrowserOnline] = useState<boolean>(false);
  const [pingOnline, setPingOnline] = useState<boolean>(false);
  const [lastOnline, setLastOnline] = useState<Date>(new Date('1970-01-01T00:00:00Z'));

  const handleOnline = () => setBrowserOnline(true);
  const handleOffline = () => setBrowserOnline(false);

  // check both navigator.onLine and actual connectivity to the backend (by regular pings every 10 sec)
  // both must succeed for the app to consider itself online
  useEffect(() => {
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    setBrowserOnline(navigator.onLine);

    (async () => {
      if (navigator.onLine) {
        setPingOnline(await ping());
      }
    })();
    
    const interval = setInterval(async () => {
      if (browserOnline) {
        setPingOnline(await ping());
      } else {
        setPingOnline(false);
      }
    }, 10000);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      clearInterval(interval);
    };
  }, [browserOnline]);

  useEffect(() => {
    onlineManager.setOnline(browserOnline && pingOnline);
    if (browserOnline && pingOnline) {
      setLastOnline(new Date());
    }
  }, [browserOnline, pingOnline, onlineManager]);

  const value = useMemo(() => ({ online: browserOnline && pingOnline, lastOnline }), [browserOnline, pingOnline, lastOnline]);

  return <OnlineStatusContext.Provider value={value}>{children}</OnlineStatusContext.Provider>;
};
